import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Button, App } from 'antd';
import { ApolloError, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { LOGIN_MUTATION } from 'graphql/mutations/auth';
import { AuthPayload } from 'graphql/types';
import { saveAuth } from 'utils/auth';
import { usePermission } from 'components/PermissionProvider';
import LanguageSwitcher from 'components/LanguageSwitcher';

const LoginPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { refresh } = usePermission();
    const { message } = App.useApp();

    const [login, { loading }] = useMutation<{ portalLogin: AuthPayload }>(LOGIN_MUTATION);

    const handleLogin = async (values: { accountNumber: string; password: string }) => {
        try {
            const { data } = await login({
                variables: {
                    accountNumber: values.accountNumber,
                    password: values.password,
                },
            });
            if (data?.portalLogin) {
                saveAuth({
                    token: data.portalLogin.token,
                    accountNumber: data.portalLogin.accountNumber,
                    mustChangePassword: data.portalLogin.mustChangePassword,
                    hasSettlementAccount: data.portalLogin.hasSettlementAccount,
                });
                // 触发 PermissionProvider 重新加载权限数据
                refresh();
                if (data.portalLogin.mustChangePassword) {
                    navigate('/change-password');
                } else {
                    navigate('/');
                }
            }
        } catch (err) {
            if (err instanceof ApolloError && err.graphQLErrors?.length > 0) {
                const gqlError = err.graphQLErrors[0];
                const messageKey = gqlError.extensions?.messageKey as string;
                const translated = messageKey ? t(messageKey) : null;
                // 如果翻译成功（不等于 key 本身），显示翻译后的提示；否则兜底
                if (translated && translated !== messageKey) {
                    message.error(translated);
                } else {
                    message.error(t('login.failed'));
                }
            } else {
                message.error(t('login.failed'));
            }
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
            <div className="bg-white rounded-2xl shadow-md p-10 w-[460px]">
                <div className="flex justify-end">
                    <LanguageSwitcher />
                </div>
                <h1 className="text-2xl font-bold mt-2">{t('login.title')}</h1>
                <p className="text-gray-400 mt-2 mb-8">{t('login.subtitle')}</p>
                <Form onFinish={handleLogin} layout="vertical">
                    <Form.Item
                        label={t('login.accountId.label')}
                        name="accountNumber"
                        rules={[{ required: true, message: t('login.accountId.required') as string }]}
                    >
                        <Input size="large" placeholder={t('login.accountId.placeholder') as string} />
                    </Form.Item>
                    <Form.Item
                        label={t('login.password.label')}
                        name="password"
                        required
                        rules={[{ required: true, message: t('login.password.required') as string }]}
                    >
                        <Input.Password size="large" placeholder={t('login.password.placeholder') as string} />
                    </Form.Item>
                    <Button
                        type="primary"
                        htmlType="submit"
                        block
                        size="large"
                        loading={loading}
                        className="rounded-lg h-12 mt-4"
                    >
                        {t('login.submit')}
                    </Button>
                </Form>
            </div>
        </div>
    );
};

export default LoginPage;
